// @ts-check

/**
 *
 * @typedef {{
*   __x_pluggable_form_integrations: any,
*   Shopify?: { shop: string }
* } & Window} CustomWindow
*
* @typedef {(formField: HTMLElement, key: string, value: FormDataEntryValue) => {key: string, value: any} | null} MapperFunction
*
* @typedef {{ proxy_url: string }} ZendeskConfig
*/

/** @type {CustomWindow} */
const cwindow = /** @type {any} */ (window)

// {
//   request: {
//     subject: "Test 2",
//     comment: {
//       body: "Hello",
//     },
//     requester: {
//       name: "Sam",
//       email: "sam@gmail.com",
//     },
//   },
// }

const zendeskApi = (() => {
 /**
  *
  * @param {ZendeskConfig} integrationSettings
  */
 function getZendeskBaseUrl(integrationSettings) {
   const BASE_URL = new URL(integrationSettings.proxy_url)
   const shop = cwindow.Shopify?.shop || BASE_URL.searchParams.get('__shop')
   if (!shop) {
     throw new Error('Zendesk integration error: Shop not found in the zendesk config')
   }

   BASE_URL.searchParams.set('__proxy', 'zendesk')
   BASE_URL.searchParams.set('__shop', shop)
   BASE_URL.pathname = '/api/v2'

   return BASE_URL
 }

 /**
  *
  * @param {{
  *   integrationSettings: ZendeskConfig,
  *   data: Record<string, any>
  * }} options
  * @returns
  */
 const request = async ({ integrationSettings, data }) => {
   const url = getZendeskBaseUrl(integrationSettings)
   url.pathname += '/requests.json'

   return fetch(url.toString(), {
     headers: { 'Content-Type': 'application/json' },
     method: 'POST',
     body: JSON.stringify({ request: data }),
   }).then((r) => r.json())
 }

 /**
  *
  * @param {{
  *   integrationSettings: ZendeskConfig,
  *   data: Record<string, any>
  * }} options
  * @returns
  */
 const ticket = async ({ integrationSettings, data }) => {
   const url = getZendeskBaseUrl(integrationSettings)
   url.pathname += '/tickets.json'

   return fetch(url.toString(), {
     headers: { 'Content-Type': 'application/json' },
     method: 'POST',
     body: JSON.stringify({ ticket: data }),
   }).then((r) => r.json())
 }

 return {
   request,
   ticket,
 }
})()

/**
*
* @param {{
*   root: HTMLElement,
*   form: HTMLFormElement,
*   integrationSettings: ZendeskConfig,
*   getFormData: (mapperFn?: MapperFunction) => FormData,
*   formDataToJson: (formData: FormData) => any,
*   submissionUid: string
* }} options
* @returns {Promise<any>}
*/

export async function submit({ root, form, getFormData, formDataToJson, integrationSettings }) {
 const fd = getFormData()
 const json = formDataToJson(fd)
 console.log('POSTing to Zendesk', {
   json,
   integrationSettings,
 })

 let subject = json.subject ?? 'Shopify Contact Form'
 let email = json.requester?.email || json.email || ''
 let commentBody = json.comment?.body || ''
 if (!email || !commentBody) {
   console.warn(`Zendesk integration: Missing required data (email, comment.body)`)
   return
 }
 commentBody = `Message:\n${commentBody}`

 let name = json?.requester?.name || json.name || ''
 if (!name && json.first_name) {
   name = json.first_name || ''
   if (json.last_name) {
     name = `${name || ''} ${json.last_name}`.trim()
   }
 }
 if (!name) name = 'Anonynmous'

 const attrs = json.attrs

 if (
   attrs &&
   typeof attrs === 'object' &&
   Object.prototype.toString.call(attrs) === '[object Object]'
 ) {
   const attrLines = Object.keys(attrs).reduce((acc, key) => {
     const value = attrs[key]
     if (value) {
       acc.push(`${key}: ${value}`)
     }
     return acc
   }, /** @type {string[]} */ ([]))

   if (attrLines.length > 0) {
     attrLines.push('')
     attrLines.push(commentBody)
     commentBody = attrLines.join('\n')
   }
 }

 const input = {
   subject: subject,
   comment: {
     body: commentBody,
   },
   requester: {
     name: name,
     email: email,
   },
 }

 const response = await zendeskApi.request({
   integrationSettings,
   data: input,
 })

 return response
}

if (!cwindow.__x_pluggable_form_integrations) {
 cwindow.__x_pluggable_form_integrations = {}
}

cwindow.__x_pluggable_form_integrations['zendesk'] = {
 submit,
}
